/**
 * @fileoverview Analytics and Reports — dashboard, reports, WIP (MOR-906)
 *
 * URL: /dashboard/timesheet-reports
 * Protection: authenticated. Firm-wide analytics scope and the WIP ledger
 * additionally require org admin/owner (enforced server-side; the UI hides
 * them otherwise).
 *
 * Composing is something you do to a surface rather than a place you go, so
 * everything is built in the one modal and the tab it was opened from decides
 * where the result lands:
 * - Dashboard — widgets and small tables, pinned to its board on save
 * - Reports — export reports, kept in the library on save and pinned to the
 *   board only by starring
 *
 * Tabs:
 * - Dashboard — actual vs chargeable, utilisation, category/person/matter
 *   breakdowns, plus the pinned board and the builder modal
 * - Reports — the saved table library, seeded with a REM pack table to build
 *   on rather than a fixed remuneration screen
 * - WIP — the aged work-in-progress ledger (admins)
 *
 * Dependencies:
 * - Convex: timesheets.reports.*
 */

import { useCallback, useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQueryOrIdle } from "@/lib/convex-query-idle";
import { useMutation } from "convex/react";
import { Plus } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { useActiveOrg } from "@/utils/org-context";
import { Button } from "@/ui/primitives";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import {
  AskAnalyticsBox,
  type AnalyticsAnswer,
} from "@/components/timesheets/AskAnalyticsBox";
import { TimesheetAnalytics } from "@/components/timesheets/TimesheetAnalytics";
import {
  BuilderDialog,
  type BuilderKind,
} from "@/components/timesheets/BuilderDialog";
import { ReportsLibrary } from "@/components/timesheets/ReportsLibrary";
import { MissingTimesheetsReport } from "@/components/timesheets/MissingTimesheetsReport";
import { WipLedger } from "@/components/timesheets/WipLedger";
import { IpsExport } from "@/components/timesheets/IpsExport";

const TABS = ["dashboard", "reports", "missing", "wip", "ips"] as const;
type ReportsTab = (typeof TABS)[number];

/**
 * The IPS export is hidden until the export set is scoped: what the IPS
 * schedules actually require, and how remuneration exports relate to them,
 * is still an open question. The tab stays in `TABS` so existing links keep
 * validating — they land on the Dashboard — and the surface itself is intact
 * behind this flag rather than deleted.
 */
const SHOW_IPS_EXPORT = false;

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/timesheet-reports",
)({
  beforeLoad: () => ({ headerTitle: "Timesheet Reports" }),
  // Tab and open builder in the URL, so views are linkable, survive reloads
  // and let the back button close the modal.
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: ReportsTab; builder?: BuilderKind } => {
    // Old links keep working: Widgets and Tables were tabs before the
    // builders moved into a modal, and the REM pack was a tab before it
    // became a starter report.
    const raw = search.tab === "rem" ? "reports" : search.tab;
    const builder = search.builder;
    return {
      tab: TABS.includes(raw as ReportsTab) ? (raw as ReportsTab) : undefined,
      builder:
        builder === "widget" || builder === "table"
          ? (builder as BuilderKind)
          : undefined,
    };
  },
  component: TimesheetReportsPage,
});

function TimesheetReportsPage() {
  const { orgId, activeOrg } = useActiveOrg();
  const isAdmin = activeOrg?.role === "owner" || activeOrg?.role === "admin";
  const { tab, builder } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // MOR-1460: the chase list is for approvers of everyone and admins.
  const { data: entryContext } = useQuery({
    ...convexQueryOrIdle(
      api.timesheets.entries.entryContext,
      orgId ? { orgId } : undefined,
    ),
    enabled: !!orgId,
  });
  const canSeeMissing = isAdmin || (entryContext?.canReportFirmWide ?? false);

  // First visit seeds the starter widgets and pivot reports as ordinary
  // saved rows (editable, renameable, deletable) — idempotent server-side.
  const ensureDefaultReports = useMutation(
    api.timesheets.reportBuilder.ensureDefaultReports,
  );
  const ensureDefaultPivotReports = useMutation(
    api.timesheets.pivotReports.ensureDefaultPivotReports,
  );
  useEffect(() => {
    if (!orgId) return;
    const args = { orgId: orgId as Id<"organizations"> };
    // Non-fatal on failure: the saved lists just start empty.
    ensureDefaultReports(args).catch(() => {});
    ensureDefaultPivotReports(args).catch(() => {});
  }, [ensureDefaultReports, ensureDefaultPivotReports, orgId]);

  // An AI answer and a pinned card are both just a spec: park it here, open
  // the builder that owns that shape, and let the builder adopt it so it
  // stays editable. Saving under the same name updates the card in place.
  const [pending, setPending] = useState<AnalyticsAnswer | null>(null);
  // Survives `pending` being consumed, so the dialog keeps naming what it
  // opened on rather than reverting to "New report" a frame later.
  const [heading, setHeading] = useState<string | null>(null);
  // The modal belongs to the Dashboard, so opening one implies that tab.
  const openBuilder = (next: BuilderKind | undefined) => {
    setHeading(null);
    void navigate({
      search: { tab: undefined, builder: next },
      replace: true,
    });
  };
  const handleAnswer = (answer: AnalyticsAnswer) => {
    setPending(answer);
    openBuilder(answer.shape === "pivot" ? "table" : "widget");
  };
  const clearPending = useCallback(() => setPending(null), []);

  /**
   * The same modal, opened from the report library: always a table, and
   * saving leaves it in the library rather than pinning it to the board.
   */
  const openReportBuilder = (report: AnalyticsAnswer | null) => {
    setPending(report);
    setHeading(report?.title ?? null);
    void navigate({
      search: { tab: "reports", builder: "table" },
      replace: true,
    });
  };
  /** Close without leaving the tab the builder was opened from. */
  const closeBuilder = () => {
    setHeading(null);
    void navigate({ search: { tab, builder: undefined }, replace: true });
  };

  // The WIP ledger and the IPS export are admin surfaces, so a shared link to
  // one lands a member on the Dashboard rather than on an empty tab — as does
  // a link to the IPS export while it is hidden.
  const unavailable =
    ((tab === "wip" || tab === "ips") && !isAdmin) ||
    (tab === "ips" && !SHOW_IPS_EXPORT) ||
    (tab === "missing" && entryContext !== undefined && !canSeeMissing);
  const activeTab = unavailable ? "dashboard" : (tab ?? "dashboard");

  if (!orgId) {
    return (
      <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
        <div className="text-sm text-muted-foreground">
          Select an organisation to view timesheet reports.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Analytics and Reports
        </h1>
        <p className="text-sm text-muted-foreground">
          Track your timesheets, generate and save reports and manage your WIP.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(next) =>
          void navigate({
            search: {
              tab: next === "dashboard" ? undefined : (next as ReportsTab),
              // Leaving the Dashboard closes its builder.
              builder: undefined,
            },
            replace: true,
          })
        }
        className="flex flex-col gap-4"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList className="w-fit">
            <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
            {canSeeMissing && (
              <TabsTrigger value="missing">Missing timesheets</TabsTrigger>
            )}
            {isAdmin && <TabsTrigger value="wip">WIP</TabsTrigger>}
            {isAdmin && SHOW_IPS_EXPORT && (
              <TabsTrigger value="ips">IPS export</TabsTrigger>
            )}
          </TabsList>
          {/* Composing is a page-level action, and what it composes depends on
              where you are: the Reports tab takes export reports, elsewhere
              widgets and small tables. The WIP ledger takes neither — it is a
              fixed statutory view, not a board. */}
          {activeTab === "reports" ? (
            <Button
              size="sm"
              className="h-8"
              onClick={() => openReportBuilder(null)}
            >
              <Plus className="size-4" />
              New report
            </Button>
          ) : (
            activeTab !== "wip" &&
            activeTab !== "missing" && (
              <Button
                size="sm"
                className="h-8"
                onClick={() => openBuilder("widget")}
              >
                <Plus className="size-4" />
                Add widget or table
              </Button>
            )
          )}
        </div>
        <TabsContent value="dashboard" className="flex flex-col gap-4">
          <AskAnalyticsBox
            orgId={orgId as Id<"organizations">}
            onAnswer={handleAnswer}
          />
          <TimesheetAnalytics
            orgId={orgId as Id<"organizations">}
            isAdmin={isAdmin}
            onEditCard={handleAnswer}
          />
        </TabsContent>
        <TabsContent value="reports">
          <ReportsLibrary
            orgId={orgId as Id<"organizations">}
            onOpenReport={openReportBuilder}
          />
        </TabsContent>
        {canSeeMissing && (
          <TabsContent value="missing">
            <MissingTimesheetsReport orgId={orgId as Id<"organizations">} />
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="wip">
            <WipLedger orgId={orgId as Id<"organizations">} />
          </TabsContent>
        )}
        {isAdmin && SHOW_IPS_EXPORT && (
          <TabsContent value="ips">
            <IpsExport orgId={orgId as Id<"organizations">} />
          </TabsContent>
        )}
      </Tabs>

      <BuilderDialog
        orgId={orgId as Id<"organizations">}
        kind={builder ?? "widget"}
        onKindChange={openBuilder}
        // On the Reports tab the modal is the report builder: tables only,
        // and starring — not saving — is what pins one to the board.
        lockKind={activeTab === "reports"}
        pinOnSave={activeTab !== "reports"}
        heading={
          activeTab === "reports" ? (heading ?? "New report") : undefined
        }
        description={
          activeTab === "reports"
            ? "Save to keep the report in the library. Star it to pin it to the Dashboard board as well."
            : undefined
        }
        open={builder !== undefined}
        onOpenChange={(next) => {
          if (!next) closeBuilder();
        }}
        pending={pending}
        onPendingConsumed={clearPending}
      />
    </div>
  );
}
